// Combo buscable genérico (typeahead por palabras, código + nombre, sin tildes).
// Mismo comportamiento/UX que el buscador de productos del inventario, pero
// reutilizable para cualquier catálogo con { id, codigo, nombre } (p.ej. cuentas
// contables). El desplegable va en un portal (position fixed) para no ser
// recortado por contenedores con overflow (celdas de tabla, etc.).
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export const normBuscar = (s: string): string => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const FIELD =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export interface ItemBuscable { id: string; codigo: string; nombre: string }

export function BuscadorCombo<T extends ItemBuscable>({
  value, onChange, items, placeholder = 'Buscar…', disabled, compact, allowClear, loading, pista,
}: {
  value: string; onChange: (id: string) => void; items: T[];
  placeholder?: string; disabled?: boolean; compact?: boolean; allowClear?: boolean; loading?: boolean;
  /** Texto tenue al final de cada fila del desplegable (p. ej. de qué catálogo
   *  viene el ítem). Sólo se ve al elegir; no entra en lo que se escribe. */
  pista?: (item: T) => string | null;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const sel = items.find((p) => p.id === value) ?? null;

  const posicionar = () => { if (inputRef.current) setRect(inputRef.current.getBoundingClientRect()); };
  const abrir = () => { if (disabled) return; setQ(''); setActiveIdx(0); posicionar(); setOpen(true); };

  useEffect(() => {
    if (!open) return;
    const upd = () => posicionar();
    const fuera = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', upd, true);
    window.addEventListener('resize', upd);
    document.addEventListener('mousedown', fuera);
    return () => { window.removeEventListener('scroll', upd, true); window.removeEventListener('resize', upd); document.removeEventListener('mousedown', fuera); };
  }, [open]);

  const filtrados = useMemo(() => {
    const words = normBuscar(q).split(/\s+/).filter(Boolean);
    if (!words.length) return items.slice(0, 60);
    return items.filter((p) => { const t = normBuscar(`${p.codigo} ${p.nombre}`); return words.every((w) => t.includes(w)); }).slice(0, 60);
  }, [q, items]);

  const elegir = (id: string) => { onChange(id); setQ(''); setOpen(false); };
  const alturaCls = compact ? 'h-9 text-sm' : '';

  return (
    <div className="relative">
      <input
        ref={inputRef}
        className={`${FIELD} ${alturaCls} ${allowClear && value && !open ? 'pr-7' : ''}`}
        value={open ? q : (sel ? (sel.codigo ? `${sel.codigo} · ${sel.nombre}` : sel.nombre) : '')}
        placeholder={loading ? 'Cargando…' : placeholder}
        disabled={disabled || loading}
        onFocus={abrir}
        onChange={(e) => { setQ(e.target.value); setActiveIdx(0); if (!open) { posicionar(); setOpen(true); } }}
        onKeyDown={(e) => {
          if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') abrir(); return; }
          if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtrados.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); const p = filtrados[activeIdx]; if (p) elegir(p.id); }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
      />
      {allowClear && value && !open && !disabled && (
        <button type="button" aria-label="Limpiar" title="Limpiar"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
          onMouseDown={(e) => { e.preventDefault(); onChange(''); }}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {open && rect && createPortal(
        <div ref={popRef} style={{ position: 'fixed', top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 300) }}
          className="z-[60] max-h-64 overflow-auto rounded border bg-background shadow-lg text-sm">
          {filtrados.length === 0 && <div className="px-2 py-2 text-muted-foreground">Sin coincidencias</div>}
          {filtrados.map((p, i) => (
            <button key={p.id} type="button"
              className={`block w-full px-2 py-1 text-left hover:bg-muted ${p.id === value ? 'bg-muted/60' : ''} ${i === activeIdx ? 'bg-muted' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); elegir(p.id); }}>
              {p.codigo && <><span className="font-mono text-xs">{p.codigo}</span> · </>}{p.nombre}
              {pista?.(p) && <span className="ml-2 text-xs text-muted-foreground">{pista(p)}</span>}
            </button>
          ))}
        </div>, document.body)}
    </div>
  );
}
